import { CommonModule } from "@angular/common";
import { Component, Inject } from "@angular/core";
import {
  VNG_DIALOG_DATA,
  VngButtonComponent,
  VngDialogActionsComponent,
  VngDialogRef,
  VngDialogTitleDirective,
  VngIconComponent,
} from "@vengreso/angular-components";
import { DialogCloseButtonComponent } from "src/app/components/dialogs/dialog-close-button/dialog-close-button.component";

/**
 * Visual flavor of the confirm dialog.
 * - `danger`: destructive actions (delete). No icon (P4 — the crimson glyph
 *   fought the orange destructive button).
 * - `info`: neutral confirmations (leave page, reset, set default). Accent icon.
 */
export type ConfirmDialogVariant = "danger" | "info";

/** Payload for {@link ConfirmDialogComponent}. Open it via `DialogService.confirm()`. */
export interface ConfirmDialogData {
  /** Headline. Rendered as HTML (call sites pass `<strong>` markup). */
  title: string;
  /** Optional secondary line under the title. Rendered as HTML. */
  message?: string;
  /** Confirm (primary) button label. Defaults to "Yes". */
  confirmLabel?: string;
  /** Cancel (secondary) button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Defaults to "info". */
  variant?: ConfirmDialogVariant;
  /** Material Symbols icon name. Defaults to "info". Ignored by the `danger` variant, which shows no icon. */
  icon?: string;
  /** Show a corner "X" close button (counts as cancel). Defaults to false. */
  showClose?: boolean;
}

/**
 * The single generic yes/no dialog for the app.
 *
 * Closes with `true` when confirmed; any other close (cancel button, X,
 * backdrop, Escape) resolves to `false` through `DialogService.confirm()`,
 * which is the intended way to open it:
 *
 * ```ts
 * this.dialog
 *   .confirm({ title: "Do you want to <strong>delete</strong> this?", variant: "danger", confirmLabel: "Delete" })
 *   .subscribe((confirmed) => confirmed && this.delete(item));
 * ```
 *
 * Replaces the former ConfirmDialog/ConfirmLeave/SkipOnboardingConfirmation/
 * ResetInFlycuts components and the `data.self[callback]` string-dispatch
 * pattern — the caller now owns the follow-up action in its subscribe.
 */
@Component({
  selector: "app-confirm-dialog",
  standalone: true,
  imports: [
    CommonModule,
    VngButtonComponent,
    VngIconComponent,
    VngDialogTitleDirective,
    VngDialogActionsComponent,
    DialogCloseButtonComponent,
  ],
  templateUrl: "./confirm-dialog.component.html",
  styleUrls: ["./confirm-dialog.component.scss"],
})
export class ConfirmDialogComponent {
  constructor(
    public dialogRef: VngDialogRef<ConfirmDialogComponent, boolean>,
    @Inject(VNG_DIALOG_DATA) public data: ConfirmDialogData
  ) {}

  get variant(): ConfirmDialogVariant {
    return this.data.variant ?? "info";
  }

  get icon(): string {
    return this.data.icon ?? "info";
  }

  get confirmLabel(): string {
    return this.data.confirmLabel ?? "Yes";
  }

  get cancelLabel(): string {
    return this.data.cancelLabel ?? "Cancel";
  }

  close(confirmed: boolean): void {
    this.dialogRef.close(confirmed);
  }
}
